import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, X } from "lucide-react";

const rovers = ["curiosity", "perseverance", "opportunity", "spirit"];

export default function MarsRover() {
  const [rover, setRover] = useState("curiosity");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    setFallback(null);
    setCachedAt(null);
    try {
      const r = await api.get("/mars/photos", { params: { rover } });
      setPhotos(r.data.photos || []);
      setFallback(r.data.fallback || null);
      setCachedAt(r.data.cached_at || null);
    } catch (e) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [rover]);

  const spotlight = photos[0];
  const grid = photos.slice(1, 20);
  const roverLabel = rover.charAt(0).toUpperCase() + rover.slice(1);

  return (
    <>
      <PageHero
        eyebrow="Mars Rover Photos"
        title="Postcards from"
        accent="another world."
        subtitle="Actual images captured by robotic explorers on the Martian surface — with the latest in spotlight and the most recent observations in the grid below."
      >
        <div className="flex flex-wrap gap-2">
          {rovers.map((r) => (
            <button
              key={r}
              onClick={() => setRover(r)}
              data-testid={`mars-rover-${r}`}
              className={`btn-pill text-[13px] ${rover === r ? "btn-primary" : "btn-secondary"}`}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {fallback === "archive" && (
          <div className="glass p-4 mb-6 text-sm text-amber-200/80 flex items-center gap-2">
            <Clock size={14} />
            The live raw feed for {roverLabel} is temporarily unavailable — showing the most recent surface imagery on record. Please check back later for the newest frames.
          </div>
        )}
        {fallback === "cached" && (
          <div className="glass p-4 mb-6 text-sm text-amber-200/80 flex items-center gap-2">
            <Clock size={14} />
            Live photos from {roverLabel} are temporarily unavailable — showing the most recent images on file{cachedAt ? ` (saved ${new Date(cachedAt).toLocaleString()})` : ""}. Please check back in a little while.
          </div>
        )}
        {fallback === "unavailable" && (
          <div className="glass p-4 mb-6 text-sm text-amber-200/80">
            The most recent photos from {roverLabel} are temporarily unavailable. Please check back in a little while.
          </div>
        )}

        {loading ? (
          <>
            <Skeleton className="h-[55vh] w-full rounded-3xl mb-6" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (<Skeleton key={i} className="h-48 w-full rounded-2xl" />))}
            </div>
          </>
        ) : photos.length === 0 ? (
          <div className="glass p-10 text-white/70" data-testid="mars-empty">
            No photos available right now. Please check back in a little while.
          </div>
        ) : (
          <>
            {/* Spotlight - latest photo with full explanation */}
            <button
              onClick={() => setSelected(spotlight)}
              className="glass-strong p-5 md:p-8 w-full text-left hover-lift fade-in-up"
              data-testid="mars-spotlight"
            >
              <div className="grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-7">
                  <img src={spotlight.img_src} alt={spotlight.title || roverLabel} className="w-full rounded-2xl" loading="lazy" />
                </div>
                <div className="lg:col-span-5">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[11px] uppercase tracking-widest text-white/80">
                    Latest · {roverLabel}
                  </div>
                  <h2 className="display mt-5 text-2xl md:text-3xl lg:text-4xl">{spotlight.title || `Sol ${spotlight.sol}: ${(spotlight.camera || {}).full_name}`}</h2>
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
                    {spotlight.sol != null && <span>Sol {spotlight.sol}</span>}
                    {spotlight.earth_date && <span>{spotlight.earth_date}</span>}
                    {(spotlight.camera || {}).full_name && <span>{spotlight.camera.full_name}</span>}
                  </div>
                  {spotlight.caption && (
                    <p className="mt-5 text-[14px] text-white/70 leading-relaxed line-clamp-6">{spotlight.caption}</p>
                  )}
                  <div className="mt-5 text-sm text-white/65">Tap to read more</div>
                </div>
              </div>
            </button>

            {/* Grid of the next 19 photos with captions */}
            {grid.length > 0 && (
              <>
                <div className="eyebrow-row mt-12 mb-6"><span className="eyebrow">More from {roverLabel}</span></div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
                  {grid.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p)}
                      className="glass hover-lift overflow-hidden text-left flex flex-col"
                      data-testid={`mars-photo-${p.id}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-white/5">
                        <img src={p.img_src} alt={p.title || roverLabel} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="text-xs text-white/45 flex flex-wrap gap-x-3">
                          {p.sol != null && <span>Sol {p.sol}</span>}
                          {p.earth_date && <span>{p.earth_date}</span>}
                        </div>
                        <div className="mt-2 text-[14px] font-semibold tracking-tight line-clamp-2">{p.title || (p.camera || {}).full_name}</div>
                        {p.caption && (
                          <div className="mt-2 text-xs text-white/55 leading-relaxed line-clamp-3">{p.caption}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-xl p-4 md:p-8 overflow-y-auto" onClick={() => setSelected(null)} data-testid="mars-modal">
          <div className="max-w-5xl mx-auto glass p-5 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 flex items-center justify-center" aria-label="Close">
              <X size={16} />
            </button>
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-7">
                <img src={selected.img_src} alt={selected.title || roverLabel} className="w-full rounded-2xl" />
              </div>
              <div className="md:col-span-5">
                <div className="eyebrow">
                  {roverLabel}
                  {selected.sol != null ? ` · Sol ${selected.sol}` : ""}
                  {selected.earth_date ? ` · ${selected.earth_date}` : ""}
                </div>
                <h3 className="display mt-3 text-2xl md:text-3xl">{selected.title || (selected.camera || {}).full_name}</h3>
                {selected.caption && (
                  <p className="mt-4 text-sm text-white/70 leading-relaxed max-h-72 overflow-y-auto pr-2">{selected.caption}</p>
                )}
                {selected.credit && (
                  <div className="mt-4 text-xs text-white/40">{selected.credit}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
